<template>
    <b-modal :id="id" size="lg" title="Prior Authorization" @show="onShow" @hide="$emit('hide')" @hidden="resetModal"
        scrollable>
        <b-container fluid v-if="value.prescription">

            <b-row v-if="lastDocumentDate != null">
                <b-col>
                    <b-alert show variant="info">
                        Last document uploaded for this patient was at {{ lastDocumentDate | formatDate('lll') }}.
                        <b>
                            <a style="color:white" @click.prevent v-b-modal.patient_documents_modal>
                                See Documents...
                            </a>
                        </b>
                    </b-alert>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <b>Patient: </b> <span>{{ patientName }}</span>
                </b-col>
                <b-col style="gap: 10px;" class="d-flex justify-content-end">
                    <router-link v-if="value?.patient?.id" target="_blank"
                        :to="{ name: 'PatientDashboard', params: { id: value.patient.id } }">
                        <span>Go To Profile</span> <i class="fas fa-user"></i>
                    </router-link>
                    <router-link target="_blank" :to="prescriptionDetailsRoute">
                        <span>Go To Rx</span> <i class="fas fa-file-prescription"></i>
                    </router-link>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Drug: </strong>{{ value.prescription.productNameShort }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Prescriber: </strong>{{ value.prescriber.lastName }}, {{ value.prescriber.firstName }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Initiated: </strong>{{ createDate }}
                </b-col>
                <b-col>
                    <strong>Last Updated: </strong>{{ updateDate }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Rx Id: </strong>{{ value.prescription.storeID }}-{{ value.prescription.rxNumber }}-{{
                        value.prescription.rfNumber
                    }}
                </b-col>
            </b-row>
            <hr>
            <b-row class="mb-2">
                <b-col>
                    <strong>Status:</strong>
                    <EnumSelector v-model="value.status" :enum-codes="PriorAuthorizationStatusCode" :disabled="!canEditPA" class="mb-0" />
                </b-col>
            </b-row>
            <b-row v-if="value.completeReason || requiresReason" class="mb-2">
                <b-col>
                    <strong>Reason:</strong>
                    <b-input
                        v-if="!completeReasonOptions.length || customReason || (value.completeReason && !completeReasonOptions.includes(value.completeReason))"
                        v-model="value.completeReason" />
                    <template v-else>
                        <b-form-select v-model="value.completeReason" :options="completeReasonOptions" />
                        <a v-if="canEnterCustomReason" href="#" @click.prevent="customReason = true">Custom
                            Reason...</a>
                    </template>
                </b-col>
            </b-row>
            <b-row class="mb-2">
                <b-col>
                    <strong>Approved Date:</strong>
                    <el-date-picker v-model="value.approvalDate" format="MM/dd/yyyy" type="date" today-button
                        reset-button />
                </b-col>
                <b-col>
                    <strong>Expiration Date:</strong>
                    <el-date-picker v-model="value.expirationDate" format="MM/dd/yyyy" type="date" today-button
                        reset-button />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>Patient Plan:</strong>
                    <SearchComponent :id="selectedPatientPlan?.id" v-model="selectedPatientPlan"
                        :search-u-r-l="`/Insurance/GetPatientPlans/` + value.patient?.id" :hide-label="true"
                        class="mt-2" clearable prepopulate />
                </b-col>
                <b-col>
                    <strong>Type:</strong>
                    <EnumSelector v-model="value.type" :enum-codes="PriorAuthorizationType" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-check v-model="value.clinicalNoteTranscription">Clinical Note Transcription</b-check>
                </b-col>
            </b-row>
            <hr />
            <b-row>
                <b-col>
                    <strong>CMM Id: </strong>{{ value.coverMyMedsId }}
                </b-col>
                <b-col class="text-right">
                    <a href="#" type="button" @click.prevent="coverMyMedsClick">
                        View Cover My Meds Profile
                    </a>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <strong>CMM Event: </strong>
                    {{ value.latestCallback.event_key }} - {{ value.latestCallback.message }}
                </b-col>
            </b-row>
            <hr />
            <b-row>
                <b-col>
                    <NotesList ref="notes-list" :entity-id="value.id" :entity-type="EntityType.PriorAuthorization"
                        :paging="false" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <NotesForm :entity-id="value.id" :entity-type="EntityType.PriorAuthorization"
                        :templates="noteTemplates" @saved="refreshNotes" />
                </b-col>
            </b-row>
            <hr>
            <CoverMyMedsResponseComponent v-model="cmmResponse" :displayValue="displayValue" />
        </b-container>

        <template #modal-footer>
            <div class="w-100 d-flex">
                <div class="d-flex flex-fill" style="gap: 6px;">
                    <b-button v-b-modal.patient_documents_modal variant="secondary" size="sm">
                        Patient Docs...
                    </b-button>
                    <b-button v-b-modal.prior-auth-fax variant="secondary" size="sm"
                        v-if="faxTemplateOptions.length && value?.prescriber?.faxNumber">
                        Fax...
                    </b-button>
                    <b-button variant="info" size="sm" v-b-modal.cmm-id-add v-if="canEditPA">
                        {{ value.coverMyMedsId ? "Update CMM Id" : "Add CMM Id" }}
                    </b-button>
                    <b-button variant="success" @click="createPA" v-if="!value.coverMyMedsId && canCreatePA" size="sm"
                        :disabled="isCreatingPA">
                        Start PA
                    </b-button>
                    <b-button variant="outline-success" @click="openDevelopHealthModal" v-if="canCreatePA" size="sm">
                        Start Develop Health
                    </b-button>
                </div>
                <div class="d-flex" style="gap: 6px;">
                    <b-button variant="primary" size="sm" @click="savePriorAuthorization">
                        <strong>Save</strong>
                    </b-button>
                </div>
            </div>
        </template>

        <patient-notes-modal-component v-if="value?.patient?.id" :entity-id="value.patient.id" />
        <doctor-notes-modal-component v-if="value?.prescriber?.id" :entity-id="value.prescriber.id" />
        <drug-notes-modal-component v-if="value?.prescription?.packageID" :entity-id="value.prescription.packageID"
            :drug-source="value.prescription.drugSource" />

        <b-modal id="cmm-id-add" :title="value.coverMyMedsId ? 'Update CMM Id' : 'Add CMM Id'" @ok="cmmOk" no-fade>
            <b-row>
                <b-col>
                    <b-input v-model="value.coverMyMedsId" />
                </b-col>
            </b-row>
        </b-modal>
        <b-modal v-if="value?.prescriber?.faxNumber" id="prior-auth-fax" title="Prior Auth Fax" no-fade
            :size="showAttachPatientDoc ? 'lg' : null" ok-title="Send Fax" scrollable
            :ok-disabled="!selectedFaxTemplate" @ok="sendFax" @hidden="resetFaxModal">
            <b-form-group label="Template">
                <b-select v-model="selectedFaxTemplate" :options="faxTemplateOptions" />
            </b-form-group>
            <b-form-group label="Sending To">
                <b-input readonly
                    :value="value.prescriber.lastName + ', ' + value.prescriber.firstName + ' (' + value.prescriber.faxNumber + ')'" />
                <b-check v-if="value.prescriber.altFaxNumber" v-model="useAltFax" class="mt-1">
                    Use Alternate Fax Number ({{ value.prescriber.altFaxNumber }})
                </b-check>
            </b-form-group>
            <b-form-group label="Notes to Include">
                <b-textarea v-model="faxNotesToInclude" />
            </b-form-group>

            <a href="#" @click.prevent="showAttachPatientDoc = !showAttachPatientDoc">
                <i :class="{ 'fa fa-fw fa-chevron-right': true, 'fa-rotate-90': showAttachPatientDoc }"
                    aria-hidden="true" style="transition: all .2s;" />
                Attach Patient Documents...
            </a>

            <PatientDocumentsForm v-show="showAttachPatientDoc" list-only selectable :patient="value?.patient"
                @selectedImageIdsUpdate="selectedFaxAttachments" />

            <template v-if="isSendingFax" #modal-footer>
                <div class="text-center w-100">
                    Sending Fax...
                    <b-spinner small />
                </div>
            </template>
        </b-modal>

        <b-modal v-model="showDevelopHealthModal" title="Start Develop Health PA" size="lg" scrollable
            :ok-disabled="isSubmittingDevelopHealth"
            ok-title="Send to Develop Health" @ok="submitToDevelopHealth" @hidden="resetDevelopHealthModal">
            <p>Select documents to send to Develop Health. Insurance and prescription data will be pulled automatically from the patient's plan and fill information.</p>

            <b-table :items="developHealthPatientDocs" :fields="developHealthDocFields" small striped>
                <template #cell(select)="row">
                    <b-form-checkbox v-model="row.item.selected" @change="onDocSelectionChange(row.item)" />
                </template>
                <template #cell(fileName)="row">
                    <strong>{{ row.value }}</strong>
                </template>
                <template #cell(docType)="row">
                    <b-form-select v-if="row.item.selected" v-model="row.item.docType"
                        :options="developHealthDocTypeOptions" size="sm" />
                    <span v-else class="text-muted">-</span>
                </template>
                <template #cell(created)="row">
                    {{ row.value | formatDate('L LT') }}
                </template>
            </b-table>

            <template v-if="isSubmittingDevelopHealth" #modal-footer>
                <div class="text-center w-100">
                    Submitting to Develop Health...
                    <b-spinner small />
                </div>
            </template>
        </b-modal>
    </b-modal>
</template>

<script lang="ts">

import { Component, Prop, Vue, Ref, Watch } from "vue-property-decorator";

import moment from "moment";
import Axios, { AxiosError } from 'axios';

import { NotificationOptions } from '@/util/NotificationOptionsPresets';

import { FaxForm, Prescription } from "@/models/Prescription";
import { PatientDocument } from "@/models/PatientDocument";
import { PatientPlan } from "@/models/PatientPlan";
import {
    PriorAuthorization,
    PriorAuthorizationStatusCode,
    CoverMyMedsResponse,
    PriorAuthStatusReason,
    PriorAuthorizationType,
} from "@/models/PriorAuthorization";

import Card from "@/components/Cards/Card.vue";
import CoverMyMedsResponseComponent from "@/components/PriorAuthorization/CoverMyMedsResponseComponent.vue";
import PatientDocumentsModal from '@/components/Patient/PatientDocumentsModal.vue';
import { DatePicker } from "element-ui";
import EnumSelector from "../EnumSelector.vue";
import SearchComponent from "../SearchComponent.vue";
import NotesList from "../Note/NotesList.vue";
import NotesForm from "../Note/NotesForm.vue";
import { EntityType } from "@/models/EntityType";
import { PriorAuthFaxTemplate } from "@/models/FaxTemplates";
import { BvModalEvent } from "bootstrap-vue";
import DoctorNotesModalComponent from "@/components/Note/DoctorNotesModalComponent.vue";
import PatientNotesModalComponent from "@/components/Note/PatientNotesModalComponent.vue";
import DrugNotesModalComponent from "@/components/Note/DrugNotesModalComponent.vue";
import { Permission } from "@/models/Permission";
import PatientDocumentsForm from "../Patient/PatientDocumentsForm.vue";

@Component({
    name: "PriorAuthorizationDetails",
    components: {
        DrugNotesModalComponent,
        PatientNotesModalComponent,
        DoctorNotesModalComponent,
        Card,
        CoverMyMedsResponseComponent,
        PatientDocumentsModal,
        [DatePicker.name]: DatePicker,
        EnumSelector,
        SearchComponent,
        NotesList,
        NotesForm,
        PatientDocumentsForm
    },
})
export default class PriorAuthorizationDetails extends Vue {
    @Ref('notes-list') protected notesList!: NotesList;
    @Prop() protected value!: PriorAuthorization;
    @Prop() private id!: string;

    private cmmResponse: CoverMyMedsResponse = {} as CoverMyMedsResponse;
    private noteTemplates: string[] = [];

    private selectedPatientPlan = new PatientPlan();
    private completeReasons: PriorAuthStatusReason[] = [];
    private customReason = false;
    private lastDocumentDate: Date | null = null;
    private isCreatingPA = false;

    protected PriorAuthorizationType = PriorAuthorizationType;
    protected PriorAuthorizationStatusCode = PriorAuthorizationStatusCode;
    protected EntityType = EntityType;

    private faxTemplates: PriorAuthFaxTemplate[] = [];
    private selectedFaxTemplate: PriorAuthFaxTemplate | null = null;
    protected faxNotesToInclude = "";

    protected useAltFax = false;
    protected showAttachPatientDoc = false;
    protected selectedPatientDocuments: number[] = [];

    // Develop Health state
    protected showDevelopHealthModal = false;
    protected isSubmittingDevelopHealth = false;
    protected developHealthPatientDocs: Array<{
        imageId: number;
        fileName: string;
        created: Date;
        selected: boolean;
        docType: string;
    }> = [];

    protected developHealthDocFields = [
        { key: 'select', label: '' },
        { key: 'fileName', label: 'File Name' },
        { key: 'docType', label: 'Document Type' },
        { key: 'created', label: 'Uploaded' }
    ];

    protected developHealthDocTypeOptions = [
        { value: '', text: 'Select type...' },
        { value: 'insurance', text: 'Insurance Card' },
        { value: 'evidence', text: 'Evidence' }
    ];

    async getPriorAuthConfigs() {
        try {
            const resp = await Axios.get<any>("/PriorAuthorization/Configs");
            this.completeReasons = resp.data.reasons;
            this.noteTemplates = resp.data.noteTemplates;
            this.faxTemplates = resp.data.faxTemplates;

        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    get canEnterCustomReason(): boolean {
        const hasPermission = this.$permissions.some((p: Permission) => p.permissionCodeName.toLowerCase() === "services/electronic pa custom reasons");
        return hasPermission;
    }

    get canCreatePA(): boolean {
        return this.$permissions.some((p: Permission) =>
            p.permissionCodeName.toLowerCase() === "services/prior authorization" ||
            p.permissionCodeName.toLowerCase() === "patient dashboard/create pa"
        );
    }

    get canEditPA(): boolean {
        return this.$permissions.some((p: Permission) =>
            p.permissionCodeName.toLowerCase() === "services/prior authorization"
        );
    }

    get hasFaxTemplates() {
        return !!this.faxTemplates.length;
    }

    get faxTemplateOptions() {
        return this.faxTemplates.filter(ft => {
            if (!ft.programs?.length) return true;
            return ft.programs.some(p => p.id === this.value.prescription?.programID);
        }).map(ft => ({ value: ft, text: ft.name }));
    }

    mounted() {
        this.getPriorAuthConfigs();
        this.selectedPatientPlan = Object.assign(new PatientPlan(), this.value.patientPlan);
    }

    get completeReasonOptions() {
        return this.completeReasons
            .filter(r => !r.status || r.status === this.value.status)
            .map(r => (r.name));
    }

    completeChosen(item: any) {
        this.value.completeReason = item;
    }

    onShow() {
        this.resetModal();

        this.$nextTick(() => {
            this.loadPatientDocuments();
        });
    }

    get status() {
        if (!this.value) return "";
        return PriorAuthorizationStatusCode[this.value.status];
    }

    get createDate() {
        if (!this.value) return "";
        return moment.utc(this.value.created).format("L");
    }

    get updateDate() {
        if (!this.value) return "";
        return moment.utc(this.value.updated).format("L");
    }

    get noteFields(): any[] {
        return [
            { key: "created", label: "", formatter: (val: any) => moment.utc(val).local().format("L LT") },
        ];
    }

    get patientName() {
        if (!this.value?.patient) return '';
        return this.value.patient.displayNamesForPerson();
    }

    get requiresReason() {
        const reasonStatuses = [
            PriorAuthorizationStatusCode.Abandoned,
            PriorAuthorizationStatusCode.Denied,
            PriorAuthorizationStatusCode.Cancelled,
        ];

        return reasonStatuses.includes(this.value.status);
    }

    resetModal() {
        this.cmmResponse = {} as CoverMyMedsResponse;
        this.customReason = false;
    }

    async savePriorAuthorization(closeModal: boolean = true) {
        try {
            if (this.requiresReason && !this.value.completeReason?.trim()) {
                this.$notification(NotificationOptions.error("A reason is required."));
                return;
            }

            const data = {
                ...this.value,
                patientPlan: this.selectedPatientPlan,
            };

            await Axios.post(`PriorAuthorization/Save`, data);

            this.$notification(NotificationOptions.successSaveNotificationPreset("Prior Authorization"));
            if (closeModal) this.close();
        } catch (err) {
            console.error('Error while saving PA', err);
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Prior Authorization", err as AxiosError));
        }
    }

    async createPA() {
        // Prevent duplicate PA creation
        if (this.isCreatingPA) {
            console.warn('PA creation already in progress');
            return;
        }

        const prescription = this.value.prescription;
        if (!prescription) return;

        this.isCreatingPA = true;

        try {
            const response = await Axios.post<PriorAuthorization>(`/PriorAuthorization/Create?paId=${this.value.id}`, prescription);

            console.log("createPa response.data: ", response.data);
            if (response.data.responses.length) {
                const resp = response.data.responses[0];
                this.cmmResponse = resp;
                this.$notification(NotificationOptions.success("Prior Authorization initiated successfully"));
            } else {
                this.$notification(NotificationOptions.warning("No responses returned."));
            }
        } catch (err) {
            this.$notification(NotificationOptions.errorSaveNotificationPreset("PA", err as AxiosError));
            console.error('Error while creating PA', err);
        } finally {
            this.isCreatingPA = false;
        }
    }

    get displayValue(): string {
        const prescription = Object.assign(new Prescription(), this.value.prescription);
        if (!prescription) return '';

        return `${prescription.rxID} ${prescription.productNameShort} - ${this.formatDate(prescription.writtenDate)}`;
    }

    formatDate(d: Date): string {
        if (!d) return "";
        return moment(d).format("L");
    }

    completeFinish() {
        if (this.value.completeReason?.length) {
            this.savePriorAuthorization();
        } else {
            console.warn("No complete reason given, not saving.");
            this.$notification(NotificationOptions.warning("No complete reason given, not saving."));
        }
    };

    // button calls
    coverMyMedsClick() {
        const url = `https://covermymeds.com/request/view/${this.value.coverMyMedsId}`;
        window.open(url, "_blank");
    }

    cmmOk() {
        this.savePriorAuthorization(false);
        this.$bvModal.hide('cmm-id-add');
    }

    close() {
        this.$bvModal.hide(this.id);
    }

    async loadPatientDocuments() {
        if (!this.value?.patient?.id) return;

        try {
            const res = await Axios.get<Array<PatientDocument>>(`/patient/${this.value?.patient?.id}/get-documents/`);
            if (res.data?.length) this.calculateLastDocumentDate(res.data);
        } catch (err) {
            console.error("Error while loading patient documents.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    calculateLastDocumentDate(docs: Array<PatientDocument>) {
        if (!docs?.length) return null;

        const res = docs.map(d => d.created).sort((d1: Date, d2: Date) => {
            return new Date(d2).getTime() - new Date(d1).getTime();
        })[0];

        this.lastDocumentDate = res;
    }

    get prescriptionDetailsRoute() {
        return {
            name: 'PrescriptionDetails',
            params: {
                storeID: this.value.prescription.storeID,
                rxNumber: this.value.prescription.rxNumber,
                rfNumber: (this.value.prescription.rfNumber || 0),
            },
        };
    }

    @Watch('value.status')
    onStatusChange(newStatus: PriorAuthorizationStatusCode, oldStatus: PriorAuthorizationStatusCode) {
        if (newStatus !== oldStatus) {
            this.value.completeReason = '';
            this.customReason = false;
        }
    }

    @Watch("value")
    handleValueChange() {
        this.selectedPatientPlan = Object.assign(new PatientPlan(), this.value.patientPlan);
    }

    @Watch("value.status")
    handleStatusChange() {
        this.customReason = false;
        if (this.value.status == PriorAuthorizationStatusCode.Approved) {
            if (!this.value.approvalDate) this.value.approvalDate = new Date();
            if (!this.value.expirationDate) this.value.expirationDate = moment().add(1, "year").toDate();
        }
    }

    protected isSendingFax = false;

    async sendFax(event: BvModalEvent) {
        event.preventDefault();

        try {
            const faxForm = new FaxForm();

            const script = Object.assign(new Prescription(), this.value.prescription);

            faxForm.fax = this.useAltFax && this.value.prescriber.altFaxNumber ?
                this.value.prescriber.altFaxNumber : this.value.prescriber.faxNumber;
            faxForm.rxID = script.rxID;
            faxForm.note = this.faxNotesToInclude;
            faxForm.imageIds = this.showAttachPatientDoc ? this.selectedPatientDocuments : [];

            this.isSendingFax = true;

            const res = await Axios.post<PatientDocument>(`PriorAuthorization/SendFax/${this.value.id}/${this.selectedFaxTemplate!.id}`, faxForm);

            if (!res.data.id) return;

            const msg = `${this.selectedFaxTemplate!.name} sent successfully and saved as a new patient document.`;

            this.$notification(
                NotificationOptions.notificationOptionsPreset(msg, NotificationOptions.NotificationTypes.success),
            );

            this.$bvModal.hide("prior-auth-fax");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isSendingFax = false;
        }
    }

    refreshNotes() {
        this.notesList.loadNotes();
    }

    selectedFaxAttachments(data: number[]) {
        this.selectedPatientDocuments = data;
    }

    resetFaxModal() {
        this.faxNotesToInclude = '';
        this.showAttachPatientDoc = false;
        this.selectedPatientDocuments = [];
    }

    // Develop Health methods
    async openDevelopHealthModal() {
        this.showDevelopHealthModal = true;
        await this.loadDevelopHealthDocs();
    }

    get developHealthDocSelections() {
        return this.developHealthPatientDocs.filter(d => d.selected);
    }

    onDocSelectionChange(item: any) {
        // Reset docType when deselected
        if (!item.selected) {
            item.docType = '';
        }
    }

    async loadDevelopHealthDocs() {
        if (!this.value?.patient?.id) return;

        try {
            const res = await Axios.get<Array<PatientDocument>>(`/patient/${this.value.patient.id}/get-documents/`);
            this.developHealthPatientDocs = (res.data || []).map(doc => ({
                imageId: doc.imageId,
                fileName: doc.fileName,
                created: doc.created,
                selected: false,
                docType: ''
            }));
        } catch (err) {
            console.error("Error loading patient documents for Develop Health", err);
        }
    }

    resetDevelopHealthModal() {
        this.developHealthPatientDocs = [];
        this.isSubmittingDevelopHealth = false;
    }

    async submitToDevelopHealth(event: BvModalEvent) {
        event.preventDefault();

        this.isSubmittingDevelopHealth = true;

        try {
            // Separate insurance and evidence documents
            const insuranceDocIds = this.developHealthDocSelections
                .filter(d => d.docType === 'insurance')
                .map(d => d.imageId);
            const evidenceDocIds = this.developHealthDocSelections
                .filter(d => d.docType === 'evidence')
                .map(d => d.imageId);

            const response = await Axios.post<{
                success: boolean;
                requestId?: string;
                portalUrl?: string;
                status?: string;
                message?: string;
            }>('/PriorAuthorization/DevelopHealth/Submit', {
                priorAuthorizationId: this.value.id,
                insuranceDocumentIds: insuranceDocIds,
                evidenceDocumentIds: evidenceDocIds
            });

            if (response.data.success) {
                let msg = response.data.message || "Successfully submitted to Develop Health.";
                if (response.data.portalUrl) {
                    msg += ` Portal URL: ${response.data.portalUrl}`;
                    // Open portal URL in new tab
                    window.open(response.data.portalUrl, "_blank");
                }
                this.$notification(NotificationOptions.success(msg));
                this.showDevelopHealthModal = false;
                this.refreshNotes();
            } else {
                this.$notification(NotificationOptions.error(response.data.message || "Submission failed."));
            }
        } catch (err) {
            console.error("Error submitting to Develop Health", err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isSubmittingDevelopHealth = false;
        }
    }
}
</script>

<style scoped>
.note-body {
    word-break: break-word;
}
</style>
